/**
 * Chat History Storage Utility - IndexedDB Implementation
 * Manages chat history in IndexedDB with much larger storage capacity
 *
 * Database structure:
 * - chats: Main chat objects with all messages embedded
 *
 * Chat Object structure:
 * {
 *   chatId: string,
 *   scopeId: string,
 *   prevChatId: string,
 *   type: string,
 *   createdAt: number,
 *   reqMessages: AssistantReqMessage[],
 *   messages: AssistantMessage[],
 *   serviceMessages: ServiceMessage[]
 * }
 */

const DB_NAME = "pallas_chat_history";
const DB_VERSION = 1;
const MAX_CHATS_PER_SCOPE = 1000;

// IndexedDB wrapper class
class ChatHistoryDB {
  constructor() {
    this.db = null;
    this.initPromise = null;
  }

  async init() {
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error("Failed to open IndexedDB:", request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // Create chats store
        if (!db.objectStoreNames.contains("chats")) {
          const chatsStore = db.createObjectStore("chats", {
            keyPath: "chatId",
          });
          chatsStore.createIndex("scopeId", "scopeId", { unique: false });
          chatsStore.createIndex(
            "scopeId_createdAt",
            ["scopeId", "createdAt"],
            { unique: false }
          );
          chatsStore.createIndex("createdAt", "createdAt", { unique: false });
        }
      };
    });

    return this.initPromise;
  }

  async ensureInitialized() {
    if (!this.db) {
      await this.init();
    }
  }

  async addOrUpdateChat(scopeId, chatObj) {
    await this.ensureInitialized();

    const transaction = this.db.transaction(["chats"], "readwrite");
    const store = transaction.objectStore("chats");

    const chatRecord = {
      ...chatObj,
      scopeId,
      createdAt: chatObj.createdAt || Date.now(),
    };

    try {
      await new Promise((resolve, reject) => {
        const request = store.put(chatRecord);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });

      // Cleanup old chats if we exceed the limit
      await this.cleanupOldChats(scopeId);

      return true;
    } catch (error) {
      console.error("Failed to add/update chat:", error);
      return false;
    }
  }

  async getChatById(scopeId, chatId) {
    if (!chatId) return null;

    await this.ensureInitialized();

    const transaction = this.db.transaction(["chats"], "readonly");
    const store = transaction.objectStore("chats");

    try {
      const chat = await new Promise((resolve, reject) => {
        const request = store.get(chatId);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });

      // Verify the chat belongs to the correct scope
      if (chat && chat.scopeId === scopeId) {
        return chat;
      }

      return null;
    } catch (error) {
      console.error("Failed to get chat by ID:", error);
      return null;
    }
  }

  async getChatHistory(scopeId, limit = 100) {
    await this.ensureInitialized();

    // Validate scopeId before using it in IDBKeyRange
    if (!scopeId) {
      return [];
    }

    const transaction = this.db.transaction(["chats"], "readonly");
    const store = transaction.objectStore("chats");
    const index = store.index("scopeId_createdAt");

    try {
      const chats = await new Promise((resolve, reject) => {
        const request = index.getAll(
          IDBKeyRange.bound([scopeId, 0], [scopeId, Date.now()])
        );
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });

      // Sort by createdAt descending and apply limit
      return chats.sort((a, b) => b.createdAt - a.createdAt).slice(0, limit);
    } catch (error) {
      console.error("Failed to get chat history:", error);
      return [];
    }
  }

  async getLatestChat(scopeId) {
    // Validate scopeId before using it
    if (!scopeId) {
      return null;
    }

    const history = await this.getChatHistory(scopeId, 1);
    return history.length > 0 ? history[0] : null;
  }

  async addReqMessageToChat(scopeId, chatId, reqMessage) {
    if (!chatId) return;

    const chat = await this.getChatById(scopeId, chatId);
    if (!chat) return;

    if (!chat.reqMessages) {
      chat.reqMessages = [];
    }

    chat.reqMessages.push(reqMessage);
    await this.addOrUpdateChat(scopeId, chat);
  }

  async addMessageToChat(scopeId, chatId, message) {
    if (!chatId) return;

    const chat = await this.getChatById(scopeId, chatId);
    if (!chat) return;

    if (!chat.messages) {
      chat.messages = [];
    }

    chat.messages.push(message);
    await this.addOrUpdateChat(scopeId, chat);
  }

  async addMessagesToChat(scopeId, chatId, messages) {
    if (!chatId || !messages || messages.length === 0) return;

    const chat = await this.getChatById(scopeId, chatId);
    if (!chat) return;

    if (!chat.messages) {
      chat.messages = [];
    }

    chat.messages.push(...messages);
    await this.addOrUpdateChat(scopeId, chat);
  }

  async addServiceMessageToChat(scopeId, chatId, serviceMessage) {
    if (!chatId) return;

    const chat = await this.getChatById(scopeId, chatId);
    if (!chat) return;

    if (!chat.serviceMessages) {
      chat.serviceMessages = [];
    }

    chat.serviceMessages.push(serviceMessage);
    await this.addOrUpdateChat(scopeId, chat);
  }

  async cleanupOldChats(scopeId) {
    const chats = await this.getChatHistory(scopeId, MAX_CHATS_PER_SCOPE + 100);

    if (chats.length <= MAX_CHATS_PER_SCOPE) {
      return;
    }

    const transaction = this.db.transaction(["chats"], "readwrite");
    const store = transaction.objectStore("chats");

    // Remove oldest chats beyond the limit
    const chatsToRemove = chats.slice(MAX_CHATS_PER_SCOPE);

    try {
      await Promise.all(
        chatsToRemove.map(
          (chat) =>
            new Promise((resolve, reject) => {
              const request = store.delete(chat.chatId);
              request.onsuccess = () => resolve();
              request.onerror = () => reject(request.error);
            })
        )
      );

      console.debug(
        `Cleaned up ${chatsToRemove.length} old chats for scope ${scopeId}`
      );
    } catch (error) {
      console.error("Failed to cleanup old chats:", error);
    }
  }

  async cleanupOldChatsByDays(days = 30) {
    await this.ensureInitialized();

    const cutoffTime = Date.now() - days * 24 * 60 * 60 * 1000;

    const transaction = this.db.transaction(["chats"], "readwrite");
    const store = transaction.objectStore("chats");
    const index = store.index("createdAt");

    try {
      // Get all chats older than cutoff time
      const oldChats = await new Promise((resolve, reject) => {
        const request = index.getAll(IDBKeyRange.upperBound(cutoffTime, true));
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });

      if (oldChats.length === 0) {
        return { deletedCount: 0 };
      }

      // Delete old chats
      await Promise.all(
        oldChats.map(
          (chat) =>
            new Promise((resolve, reject) => {
              const deleteRequest = store.delete(chat.chatId);
              deleteRequest.onsuccess = () => resolve();
              deleteRequest.onerror = () => reject(deleteRequest.error);
            })
        )
      );

      console.debug(
        `Cleaned up ${oldChats.length} chats older than ${days} days`
      );

      return { deletedCount: oldChats.length };
    } catch (error) {
      console.error("Failed to cleanup old chats by days:", error);
      return { deletedCount: 0, error: error.message };
    }
  }

  async clearAllHistory() {
    await this.ensureInitialized();

    const transaction = this.db.transaction(["chats"], "readwrite");
    const store = transaction.objectStore("chats");

    try {
      await new Promise((resolve, reject) => {
        const request = store.clear();
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error("Failed to clear chat history:", error);
    }
  }

  async getStorageStats() {
    try {
      const estimate = await navigator.storage.estimate();

      // Count chats in IndexedDB
      await this.ensureInitialized();
      const transaction = this.db.transaction(["chats"], "readonly");
      const store = transaction.objectStore("chats");

      const chatCount = await new Promise((resolve, reject) => {
        const request = store.count();
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });

      return {
        totalSize: estimate.usage || 0,
        quota: estimate.quota || 0,
        usageRatio: estimate.quota ? estimate.usage / estimate.quota : 0,
        chatCount,
        isNearLimit: estimate.quota
          ? estimate.usage / estimate.quota > 0.8
          : false,
      };
    } catch (error) {
      console.error("Failed to get storage stats:", error);
      return {
        totalSize: 0,
        quota: 0,
        usageRatio: 0,
        chatCount: 0,
        isNearLimit: false,
      };
    }
  }
}

// Global instance
const chatDB = new ChatHistoryDB();

// Initialize IndexedDB on first load
let initPromise = null;
const ensureInitialized = async () => {
  if (initPromise) {
    return initPromise;
  }

  initPromise = chatDB.init();
  return initPromise;
};

// Public API
export const getChatHistory = async (scopeId) => {
  if (!scopeId) {
    return [];
  }
  await ensureInitialized();
  return await chatDB.getChatHistory(scopeId);
};

export const saveChatHistory = async (scopeId, history) => {
  await ensureInitialized();

  // Clear existing history and save new one
  const existingChats = await chatDB.getChatHistory(scopeId);

  // Remove existing chats for this scope
  for (const chat of existingChats) {
    // Implementation would delete each chat, but for simplicity we'll just overwrite
  }

  // Add all chats from history
  for (const chat of history) {
    await chatDB.addOrUpdateChat(scopeId, chat);
  }

  return history;
};

export const addOrUpdateChat = async (scopeId, chatObj) => {
  await ensureInitialized();
  await chatDB.addOrUpdateChat(scopeId, chatObj);

  // Return full history for backward compatibility
  return await getChatHistory(scopeId);
};

export const getChatById = async (scopeId, chatId) => {
  await ensureInitialized();
  return await chatDB.getChatById(scopeId, chatId);
};

export const addReqMessageToChat = async (scopeId, chatId, reqMessage) => {
  await ensureInitialized();
  await chatDB.addReqMessageToChat(scopeId, chatId, reqMessage);
};

export const addMessageToChat = async (scopeId, chatId, message) => {
  await ensureInitialized();
  await chatDB.addMessageToChat(scopeId, chatId, message);
};

export const addMessagesToChat = async (scopeId, chatId, messages) => {
  await ensureInitialized();
  await chatDB.addMessagesToChat(scopeId, chatId, messages);
};

export const addServiceMessageToChat = async (
  scopeId,
  chatId,
  serviceMessage
) => {
  await ensureInitialized();
  await chatDB.addServiceMessageToChat(scopeId, chatId, serviceMessage);
};

export const clearChatHistory = async () => {
  await ensureInitialized();
  await chatDB.clearAllHistory();
};

export const getLatestChat = async (scopeId) => {
  if (!scopeId) {
    return null;
  }
  await ensureInitialized();
  return await chatDB.getLatestChat(scopeId);
};

export const getChatId = async (scopeId) => {
  if (!scopeId) {
    return null;
  }
  await ensureInitialized();
  const latestChat = await chatDB.getLatestChat(scopeId);
  return latestChat?.chatId || null;
};

export const getStorageUsage = async () => {
  await ensureInitialized();
  return await chatDB.getStorageStats();
};

export const cleanupStorage = async () => {
  await ensureInitialized();
  try {
    // Get all scopes and clean each one
    const transaction = chatDB.db.transaction(["chats"], "readonly");
    const store = transaction.objectStore("chats");
    const index = store.index("scopeId");

    const scopeIds = new Set();
    await new Promise((resolve, reject) => {
      const request = index.openCursor();
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          scopeIds.add(cursor.value.scopeId);
          cursor.continue();
        } else {
          resolve();
        }
      };
      request.onerror = () => reject(request.error);
    });

    // Cleanup each scope
    for (const scopeId of scopeIds) {
      await chatDB.cleanupOldChats(scopeId);
    }

    return true;
  } catch (error) {
    console.error("Failed to cleanup storage:", error);
    return false;
  }
};

export const cleanupChatsByDays = async (days = 30) => {
  await ensureInitialized();
  return await chatDB.cleanupOldChatsByDays(days);
};

// Landing state management using localStorage
const LANDING_STORAGE_KEY = "pallas_chat_landing";

/**
 * Get landing prevChatId for a specific scope
 * @param {string} scopeId - The scope ID
 * @param {string} defaultValue - Default value if no landing state exists
 * @returns {string|null} Landing prevChatId or null
 */
export const getLanding = (scopeId, defaultValue = null) => {
  try {
    const landingData = localStorage.getItem(LANDING_STORAGE_KEY);
    if (!landingData) {
      return defaultValue;
    }

    const parsed = JSON.parse(landingData);
    return parsed[scopeId] !== undefined ? parsed[scopeId] : defaultValue;
  } catch (error) {
    console.error("Failed to get landing state:", error);
    return defaultValue;
  }
};

/**
 * Set landing prevChatId for a specific scope
 * @param {string} scopeId - The scope ID
 * @param {string|null} prevChatId - Previous chat ID or null to clear
 * @returns {boolean} Success status
 */
export const setLanding = (scopeId, prevChatId) => {
  try {
    let landingData = {};

    const existing = localStorage.getItem(LANDING_STORAGE_KEY);
    if (existing) {
      landingData = JSON.parse(existing);
    }

    if (prevChatId === null || prevChatId === undefined) {
      // Remove the entry if null/undefined
      delete landingData[scopeId];
    } else {
      landingData[scopeId] = prevChatId;
    }

    localStorage.setItem(LANDING_STORAGE_KEY, JSON.stringify(landingData));

    return true;
  } catch (error) {
    console.error("Failed to set landing state:", error);
    return false;
  }
};
